import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Dimensions,
  FlatList,
  Image,
  Linking,
  Modal,
  Pressable,
  StatusBar,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { onAuthStateChanged } from 'firebase/auth';
import TonyteBackground from '../components/TonyteBackground';
import PartnerQrSheet from '../components/PartnerQrSheet';
import PhysicalLightSwitch from '../components/PhysicalLightSwitch';
import AmbientGlow from '../components/AmbientGlow';
import LoadingSpinner from '../components/LoadingSpinner';
import { Colors, Radius, Typography } from '../theme';
import type { RootStackParamList, TonyteActivity, VenueSummary } from '../types';
import { resolveActivityImage } from '../utils/activityImages';
import { getResolvedEventId } from '../utils/eventIds';
import { ANALYTICS_SOURCES, trackEventDetailOpen } from '../utils/eventAnalytics';
import { buildGoogleMapsUrl } from '../utils/activityActions';
import { isActivityCurrentOrUpcoming, parseActivityDate, sortActivitiesByStart, toLocalDateKey } from '../utils/activityDates';
import { PICKER_LOCALE_BY_LANGUAGE } from '../utils/pickerFieldDate';
import {
  buildPartnerVenueSummaries,
  buildVenueSummaries,
  venueKeyForActivity,
  getCollabLabel,
  partnerVenueEvents,
  splitVenueEvents,
  useEventPublicStats,
  useVenueFollow,
  useVenuePostInteractions,
  useVenueVibes,
} from '../hooks/useVenueVibes';
import { auth, functionsEu, httpsCallable } from '../firebase';
import { normalizeActivity } from '../hooks/useFeed';
import { useProfile } from '../hooks/useProfile';
import { useI18n } from '../i18n';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/types';
import { useEventUploaderImage } from '../hooks/useEventUploaderImage';
import { TONYTE_PROFILE_IMAGE } from '../utils/userProfiles';
import RatingSummary from '../features/partnerReviews/RatingSummary';
import ReviewsTab, { type ReviewsTabHandle } from '../features/partnerReviews/ReviewsTab';
import { usePartnerReviewStats } from '../features/partnerReviews/usePartnerReviews';
import { joinPartnerCommunity } from '../services/partnerCommunities';

type Props = NativeStackScreenProps<RootStackParamList, 'VenueProfile'>;
type TabKey = 'upcoming' | 'past' | 'calendar' | 'reviews';

const { width } = Dimensions.get('window');
const GRID_GAP = 3;
const GRID_HORIZONTAL_PADDING = 0;
const POST_SIZE = Math.floor((width - GRID_HORIZONTAL_PADDING * 2 - GRID_GAP * 2) / 3);
// 2.5:1 — the shape partners crop their cover to (EditVenueProfileScreen) and
// the Vibe at Venues card uses, so the header shows exactly their framing.
const COVER_HEIGHT = Math.round(width / 2.5);
// Blurred cover shown below the sharp cover before the fade into the page.
const COVER_BOTTOM_BAND = 28;

// Midnight stays the canonical StyleSheet; Daylight only overrides (see
// createThemeStyles at the bottom).
function useAppearance() {
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const icon = (dark: string) => (theme.isDark ? dark : theme.colors.icon);
  const iconMuted = (dark: string) => (theme.isDark ? dark : theme.colors.iconMuted);
  return { theme, appearance, icon, iconMuted };
}

const CALENDAR_CELL = Math.floor((width - 36) / 7); // profileBlock has 18px side padding

// Sync-imported events carry their Berlin-local date as startDate; prefer it so
// the calendar shows the venue's day regardless of the viewer's timezone.
const eventDayKey = (event: TonyteActivity) =>
  (event.startDate && /^\d{4}-\d{2}-\d{2}$/.test(event.startDate) ? event.startDate : parseActivityDate(event).dateKey);

function VenueCalendar({ events, onOpenEvent, onEditEvent, footer }: {
  events: TonyteActivity[];
  onOpenEvent: (event: TonyteActivity) => void;
  // Set only for viewers who may edit this event (its partner, or an admin).
  onEditEvent?: (event: TonyteActivity) => (() => void) | undefined;
  footer?: React.ReactNode;
}) {
  const { t, language } = useI18n();
  const { appearance, icon } = useAppearance();
  const locale = PICKER_LOCALE_BY_LANGUAGE[language] ?? 'en-GB';
  const todayKey = toLocalDateKey(new Date());

  const eventsByDay = useMemo(() => {
    const map = new Map<string, TonyteActivity[]>();
    [...events].sort(sortActivitiesByStart).forEach(event => {
      const key = eventDayKey(event);
      if (!key) return;
      map.set(key, [...(map.get(key) ?? []), event]);
    });
    return map;
  }, [events]);

  // Open on the next day with events (or today), so the calendar never lands empty.
  const initialKey = useMemo(
    () => [...eventsByDay.keys()].sort().find(key => key >= todayKey) ?? todayKey,
    [eventsByDay, todayKey],
  );
  const monthOf = (key: string) => new Date(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, 1);
  const [selectedKey, setSelectedKey] = useState(initialKey);
  const [month, setMonth] = useState(() => monthOf(initialKey));
  const [userNavigated, setUserNavigated] = useState(false);
  // List = every published event of this partner, scrollable by day (what
  // most visitors want); Month = the grid + one day.
  const [view, setView] = useState<'list' | 'month'>('list');
  const [listScope, setListScope] = useState<'upcoming' | 'past'>('upcoming');
  const listDays = useMemo(() => {
    const keys = [...eventsByDay.keys()].filter(key => (listScope === 'upcoming' ? key >= todayKey : key < todayKey)).sort();
    return listScope === 'past' ? keys.reverse() : keys;
  }, [eventsByDay, listScope, todayKey]);
  const dayParts = (key: string) => {
    const date = new Date(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, Number(key.slice(8, 10)));
    return {
      date: date.toLocaleDateString(locale, { month: 'short', day: 'numeric' }),
      weekday: key === todayKey ? t('partnerCalendar.today') : date.toLocaleDateString(locale, { weekday: 'long' }),
    };
  };
  const renderEventRow = (event: TonyteActivity) => (
    <CalendarListCard key={getResolvedEventId(event)} event={event} onPress={() => onOpenEvent(event)} onEdit={onEditEvent?.(event)} />
  );
  // Luma-style agenda: a timeline rail with one node per day.
  const renderDay = (key: string, index: number, total: number) => {
    const parts = dayParts(key);
    return (
      <View key={key} style={styles.agendaDay}>
        <View style={styles.agendaRail}>
          <View style={[styles.agendaDot, appearance.agendaDot, key === todayKey && styles.agendaDotToday]} />
          {index < total - 1 ? <View style={[styles.agendaLine, appearance.agendaLine]} /> : null}
        </View>
        <View style={styles.agendaBody}>
          <Text style={styles.agendaHeading}>
            <Text style={[styles.agendaDate, appearance.calendarMonth]}>{parts.date}</Text>
            <Text style={[styles.agendaWeekday, appearance.calendarEmpty, key === todayKey && styles.agendaWeekdayToday]}>{`  ${parts.weekday}`}</Text>
          </Text>
          <View style={styles.calendarListEvents}>{(eventsByDay.get(key) ?? []).map(renderEventRow)}</View>
        </View>
      </View>
    );
  };

  // Events can arrive after mount (e.g. the venue's event history); keep following the
  // first event day until the user picks a day or changes month themselves.
  useEffect(() => {
    if (userNavigated) return;
    setSelectedKey(initialKey);
    setMonth(monthOf(initialKey));
  }, [initialKey, userNavigated]);

  const cells = useMemo(() => {
    const leading = (month.getDay() + 6) % 7; // Monday-first weeks
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const days: (Date | null)[] = Array.from({ length: leading }, () => null);
    for (let day = 1; day <= daysInMonth; day += 1) days.push(new Date(month.getFullYear(), month.getMonth(), day));
    while (days.length % 7) days.push(null);
    return days;
  }, [month]);

  const weekdays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => new Date(2024, 0, 1 + index).toLocaleDateString(locale, { weekday: 'narrow' })),
    [locale],
  );
  const selectedEvents = eventsByDay.get(selectedKey) ?? [];
  const shiftMonth = (delta: number) => {
    setUserNavigated(true);
    setMonth(current => new Date(current.getFullYear(), current.getMonth() + delta, 1));
  };
  const selectDay = (key: string) => {
    setUserNavigated(true);
    setSelectedKey(key);
  };

  const viewToggle = (
    <View style={styles.calendarToggleRow}>
      {(['list', 'month'] as const).map(item => (
        <TouchableOpacity
          key={item}
          onPress={() => setView(item)}
          style={[styles.calendarToggle, appearance.calendarNav, view === item && styles.calendarToggleActive, view === item && appearance.calendarSelected]}
          accessibilityRole="tab"
          accessibilityState={{ selected: view === item }}
        >
          <Icon name={item === 'list' ? 'list' : 'calendar'} size={14} color={view === item ? '#fff' : icon('#fff')} />
          <Text style={[styles.calendarToggleText, appearance.calendarDayText, view === item && styles.calendarDayTextSelected]}>{t(`partnerCalendar.view.${item}`)}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  if (view === 'list') {
    return (
      <View style={styles.calendar}>
        {viewToggle}
        <View style={styles.calendarToggleRow}>
          {(['upcoming', 'past'] as const).map(item => (
            <TouchableOpacity key={item} onPress={() => setListScope(item)} accessibilityRole="tab" accessibilityState={{ selected: listScope === item }}>
              <Text style={[styles.calendarScope, appearance.calendarEmpty, listScope === item && styles.calendarScopeActive, listScope === item && appearance.calendarMonth]}>
                {t(`venueProfiles.${item}`)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        {listDays.length ? listDays.map((key, index) => renderDay(key, index, listDays.length)) : <Text style={[styles.calendarEmpty, appearance.calendarEmpty]}>{t('partnerCalendar.noEvents')}</Text>}
        {footer}
      </View>
    );
  }

  return (
    <View style={styles.calendar}>
      {viewToggle}
      <View style={styles.calendarHeader}>
        <TouchableOpacity onPress={() => shiftMonth(-1)} style={[styles.calendarNav, appearance.calendarNav]}>
          <Icon name="chevron-left" size={18} color={icon('#fff')} />
        </TouchableOpacity>
        <Text style={[styles.calendarMonth, appearance.calendarMonth]}>{month.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}</Text>
        <TouchableOpacity onPress={() => shiftMonth(1)} style={[styles.calendarNav, appearance.calendarNav]}>
          <Icon name="chevron-right" size={18} color={icon('#fff')} />
        </TouchableOpacity>
      </View>
      <View style={styles.calendarGrid}>
        {weekdays.map((label, index) => <Text key={`w${index}`} style={[styles.calendarWeekday, appearance.calendarWeekday]}>{label}</Text>)}
        {cells.map((date, index) => {
          if (!date) return <View key={`e${index}`} style={styles.calendarCell} />;
          const key = toLocalDateKey(date);
          const hasEvents = eventsByDay.has(key);
          const selected = key === selectedKey;
          return (
            <TouchableOpacity key={key} onPress={() => selectDay(key)} style={styles.calendarCell}>
              <View style={[styles.calendarDay, key === todayKey && styles.calendarToday, key === todayKey && appearance.calendarToday, selected && styles.calendarSelected, selected && appearance.calendarSelected]}>
                <Text style={[styles.calendarDayText, appearance.calendarDayText, !hasEvents && styles.calendarDayMuted, !hasEvents && appearance.calendarDayMuted, selected && styles.calendarDayTextSelected]}>{date.getDate()}</Text>
              </View>
              <View style={[styles.calendarDot, appearance.calendarDot, !hasEvents && styles.calendarDotHidden]} />
            </TouchableOpacity>
          );
        })}
      </View>
      <View style={styles.calendarEvents}>
        {selectedEvents.length ? selectedEvents.map(renderEventRow) : <Text style={[styles.calendarEmpty, appearance.calendarEmpty]}>{t('venueProfiles.noEventsOnDay')}</Text>}
      </View>
      {footer}
    </View>
  );
}

type VenueFollower = { userId: string; name: string; username: string; photoUrl: string };

// Owner/admin-only follower list. Both calls are re-authorized server-side
// (functions/venueFollowers.js); this screen only decides whether to offer it.
function FollowersModal({ venue, visible, onClose }: { venue: VenueSummary; visible: boolean; onClose: () => void }) {
  const [followers, setFollowers] = useState<VenueFollower[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const { appearance, icon, theme } = useAppearance();

  useEffect(() => {
    if (!visible) return undefined;
    let active = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        await auth.currentUser?.getIdToken(true);
        const list = httpsCallable<{ venueId: string }, { followers: VenueFollower[] }>(functionsEu, 'listVenueFollowersEu');
        const { data } = await list({ venueId: venue.id });
        if (active) setFollowers(data.followers);
      } catch (err) {
        console.warn('[Tonyte][VenueFollowers] list failed', err);
        if (active) setError('Could not load followers. Please try again.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [venue.id, visible]);

  const removeFollower = (follower: VenueFollower) => {
    Alert.alert(
      'Remove follower?',
      `${follower.name} will no longer follow ${venue.name}. They won't be notified.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            setRemovingId(follower.userId);
            try {
              await auth.currentUser?.getIdToken(true);
              const remove = httpsCallable<{ venueId: string; userId: string }, { removed: boolean }>(functionsEu, 'removeVenueFollowerEu');
              await remove({ venueId: venue.id, userId: follower.userId });
              setFollowers(current => current.filter(item => item.userId !== follower.userId));
            } catch (err) {
              console.warn('[Tonyte][VenueFollowers] remove failed', err);
              Alert.alert('Could not remove follower', 'Please try again.');
            } finally {
              setRemovingId(null);
            }
          },
        },
      ],
    );
  };

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <Pressable style={[styles.previewBackdrop, appearance.previewBackdrop]} onPress={onClose}>
        <Pressable style={[styles.followersSheet, appearance.followersSheet]} onPress={() => {}}>
          <View style={styles.modalHeader}>
            <Text style={[styles.followersTitle, appearance.followersTitle]}>Followers{loading || error ? '' : ` (${followers.length})`}</Text>
            <Pressable onPress={onClose} style={[styles.closeButton, appearance.closeButton]}>
              <Icon name="x" size={18} color={icon('#fff')} />
            </Pressable>
          </View>
          {loading ? <ActivityIndicator color={theme.isDark ? '#fff' : theme.colors.accent} style={styles.followersStatus} /> : null}
          {error ? <Text style={[styles.followersEmpty, appearance.followersEmpty, styles.followersStatus]}>{error}</Text> : null}
          {!loading && !error ? (
            <FlatList
              data={followers}
              keyExtractor={item => item.userId}
              ListEmptyComponent={<Text style={[styles.followersEmpty, appearance.followersEmpty, styles.followersStatus]}>No followers yet.</Text>}
              renderItem={({ item }) => (
                <View style={styles.followerRow}>
                  <View style={[styles.followerAvatar, appearance.followerAvatar]}>
                    {item.photoUrl
                      ? <Image source={{ uri: item.photoUrl }} style={styles.followerAvatarImage} />
                      : <Text style={[styles.followerAvatarText, appearance.followerAvatarText]}>{item.name.slice(0, 1).toUpperCase()}</Text>}
                  </View>
                  <View style={styles.followerText}>
                    <Text style={[styles.followerName, appearance.followerName]} numberOfLines={1}>{item.name}</Text>
                    {item.username ? <Text style={[styles.followerUsername, appearance.followerUsername]} numberOfLines={1}>@{item.username}</Text> : null}
                  </View>
                  <TouchableOpacity
                    disabled={removingId === item.userId}
                    onPress={() => removeFollower(item)}
                    style={[styles.followerRemove, appearance.followerRemove]}
                  >
                    {removingId === item.userId
                      ? <ActivityIndicator size="small" color={icon('#fff')} />
                      : <Text style={[styles.followerRemoveText, appearance.followerRemoveText]}>Remove</Text>}
                  </TouchableOpacity>
                </View>
              )}
            />
          ) : null}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const toImageSource = (value: number | string | null) => {
  if (!value) return null;
  return typeof value === 'number' ? value : { uri: value };
};

// One agenda entry: time, title, host, place and entry price, cover on the right.
function CalendarListCard({ event, onPress, onEdit }: { event: TonyteActivity; onPress: () => void; onEdit?: () => void }) {
  const { t } = useI18n();
  const { appearance, iconMuted } = useAppearance();
  const [image, setImage] = useState<number | string | null>(null);
  const stats = useEventPublicStats(getResolvedEventId(event));
  useEffect(() => {
    let active = true;
    resolveActivityImage(event).then(resolved => { if (active) setImage(resolved.source); }).catch(() => { if (active) setImage(null); });
    return () => { active = false; };
  }, [event]);
  const source = toImageSource(image);
  const host = event.organizerName || event.hostName || event.venueName || '';
  // Same rule as the feed card: the partner's own picture, Tonyte logo only
  // for admin events. Not event.organizerLogoUrl, which can hold whoever last
  // edited the event.
  const { kind: uploaderKind, image: hostLogo } = useEventUploaderImage(event);
  const [hostLogoFailed, setHostLogoFailed] = useState(false);
  useEffect(() => { setHostLogoFailed(false); }, [hostLogo]);
  const place = event.locationName || event.address || '';
  const time = [event.startTime, event.endTime && event.endTime !== event.startTime ? event.endTime : null].filter(Boolean).join(' – ');
  const rawPrice = String(event.ticketPrice || (event as any).price || '').trim();
  const price = event.isFree === true || rawPrice === '0' ? t('partnerCalendar.free')
    : rawPrice ? (/^\d+([.,]\d+)?$/.test(rawPrice) ? `€${rawPrice}` : rawPrice) : '';
  const interested = stats.interestedCount || event.interestedCount || 0;
  return (
    <TouchableOpacity onPress={onPress} activeOpacity={0.85} style={[styles.agendaCard, appearance.agendaCard]} accessibilityRole="button">
      <View style={styles.agendaCardCopy}>
        {time ? <Text style={[styles.agendaTime, appearance.calendarEmpty]}>{time}</Text> : null}
        <Text style={[styles.agendaTitle, appearance.calendarEventTitle]} numberOfLines={2}>{event.title}</Text>
        {host ? (
          <View style={styles.agendaMetaRow}>
            {hostLogo && !hostLogoFailed ? (
              <Image source={{ uri: hostLogo }} style={styles.agendaHostLogo} resizeMethod="resize" onError={() => setHostLogoFailed(true)} />
            ) : uploaderKind === 'admin' ? (
              <Image source={TONYTE_PROFILE_IMAGE} style={styles.agendaHostLogo} resizeMode="contain" />
            ) : (
              <View style={[styles.agendaHostLogo, styles.agendaHostFallback]}><Text style={styles.agendaHostLetter}>{host.slice(0, 1).toUpperCase()}</Text></View>
            )}
            <Text style={[styles.agendaMeta, appearance.calendarEmpty]} numberOfLines={1}>{t('partnerCalendar.by').replace('{name}', host)}</Text>
          </View>
        ) : null}
        {place ? (
          <View style={styles.agendaMetaRow}>
            <Icon name="map-pin" size={13} color={iconMuted('rgba(255,255,255,0.55)')} />
            <Text style={[styles.agendaMeta, appearance.calendarEmpty]} numberOfLines={1}>{place}</Text>
          </View>
        ) : null}
        {price || interested || onEdit ? (
          <View style={styles.agendaFooter}>
            {price ? <View style={[styles.agendaPrice, price === t('partnerCalendar.free') && styles.agendaPriceFree]}><Text style={[styles.agendaPriceText, price === t('partnerCalendar.free') && styles.agendaPriceFreeText]}>{price}</Text></View> : null}
            {interested ? (
              <View style={styles.agendaMetaRow}>
                <Icon name="star" size={12} color={iconMuted('rgba(255,255,255,0.55)')} />
                <Text style={[styles.agendaMeta, appearance.calendarEmpty]}>{t('partnerCalendar.interestedCount').replace('{count}', String(interested))}</Text>
              </View>
            ) : null}
            {onEdit ? (
              <TouchableOpacity
                onPress={onEdit}
                style={[styles.agendaEdit, appearance.agendaEdit]}
                accessibilityRole="button"
                accessibilityLabel={`${t('common.edit')}: ${event.title}`}
                hitSlop={6}
              >
                <Icon name="edit-2" size={12} color={iconMuted('rgba(255,255,255,0.85)')} />
                <Text style={[styles.agendaEditText, { color: iconMuted('rgba(255,255,255,0.85)') }]}>{t('common.edit')}</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ) : null}
      </View>
      {source ? <Image source={source} style={styles.agendaCover} resizeMode="cover" /> : (
        <LinearGradient colors={['#7C3AED', '#F27D26']} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={styles.agendaCover}>
          <Icon name="calendar" size={26} color="rgba(255,255,255,0.9)" />
        </LinearGradient>
      )}
    </TouchableOpacity>
  );
}

function EventPostTile({ event, onPress, onLongPress }: { event: TonyteActivity; onPress: () => void; onLongPress: () => void }) {
  const [image, setImage] = useState<number | string | null>(null);
  const stats = useEventPublicStats(getResolvedEventId(event));
  useEffect(() => {
    let active = true;
    resolveActivityImage(event).then(resolved => {
      if (active) setImage(resolved.source);
    }).catch(() => {
      if (active) setImage(null);
    });
    return () => { active = false; };
  }, [event]);

  const source = toImageSource(image);
  return (
    <TouchableOpacity activeOpacity={0.88} onPress={onPress} onLongPress={onLongPress} delayLongPress={260} style={styles.postTile}>
      {source ? <Image source={source} style={StyleSheet.absoluteFill} resizeMode="cover" resizeMethod="resize" /> : <LinearGradient colors={['#35136A', '#111827']} style={StyleSheet.absoluteFill} />}
      <LinearGradient colors={['rgba(0,0,0,0.06)', 'rgba(0,0,0,0.64)']} style={StyleSheet.absoluteFill} />
      <View style={styles.collabPill}>
        <Icon name="users" size={10} color="#fff" />
        <Text style={styles.collabText} numberOfLines={1}>{getCollabLabel(event)}</Text>
      </View>
      <Text style={styles.postTitle} numberOfLines={2}>{event.title}</Text>
      <View style={styles.postStats}>
        <Icon name="heart" size={10} color="#fff" />
        <Text style={styles.postStatsText}>{stats.interestedCount}</Text>
        <Icon name="message-circle" size={10} color="#fff" />
        <Text style={styles.postStatsText}>{stats.chatMembersCount}</Text>
      </View>
    </TouchableOpacity>
  );
}

function PostPreviewModal({
  event,
  visible,
  onClose,
  onOpenEvent,
}: {
  event: TonyteActivity | null;
  visible: boolean;
  onClose: () => void;
  onOpenEvent: (event: TonyteActivity) => void;
}) {
  const { t } = useI18n();
  const userId = auth.currentUser?.uid || null;
  const [image, setImage] = useState<number | string | null>(null);
  const interactions = useVenuePostInteractions(event, userId);
  const { appearance, icon } = useAppearance();

  useEffect(() => {
    if (!event) return;
    let active = true;
    resolveActivityImage(event).then(resolved => {
      if (active) setImage(resolved.source);
    }).catch(() => {
      if (active) setImage(null);
    });
    return () => { active = false; };
  }, [event]);

  if (!event) return null;
  const source = toImageSource(image);

  return (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={onClose}>
      <Pressable style={[styles.previewBackdrop, appearance.previewBackdrop]} onPress={onClose}>
        <Pressable style={[styles.previewCard, appearance.previewCard]} onPress={() => {}}>
            {source ? <Image source={source} style={styles.modalImage} resizeMode="cover" resizeMethod="resize" /> : <LinearGradient colors={['#35136A', '#111827']} style={styles.modalImage} />}
            <View style={styles.modalBody}>
              <View style={styles.modalHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.modalTitle, appearance.modalTitle]}>{event.title}</Text>
                  <Text style={[styles.modalMeta, appearance.modalMeta]}>{event.venueName || event.locationName || event.city}</Text>
                </View>
                <Pressable onPress={onClose} style={[styles.closeButton, appearance.closeButton]}>
                  <Icon name="x" size={18} color={icon('#fff')} />
                </Pressable>
              </View>
              <View style={styles.actionRow}>
                <TouchableOpacity disabled={interactions.pending} onPress={interactions.toggleLike} style={[styles.actionButton, appearance.actionButton]}>
                  <Icon name="heart" size={18} color={interactions.liked ? '#FF6BAA' : icon('#fff')} />
                  <Text style={[styles.actionText, appearance.actionText]}>{interactions.likesCount}</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => onOpenEvent(event)} style={[styles.actionButton, appearance.actionButton]}>
                  <Icon name="message-circle" size={18} color={icon('#fff')} />
                  <Text style={[styles.actionText, appearance.actionText]}>{interactions.commentsCount}</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={interactions.sharePost} style={[styles.actionButton, appearance.actionButton]}>
                  <Icon name="send" size={18} color={icon('#fff')} />
                  <Text style={[styles.actionText, appearance.actionText]}>{interactions.sharesCount}</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => onOpenEvent(event)} style={styles.eventButton}>
                  <Text style={styles.eventButtonText}>{t('venueProfiles.open')}</Text>
                </TouchableOpacity>
              </View>
            </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// Venue event history per venueId for this app session, so reopening a
// profile shows its full counts on the first frame (refreshed in background).
const venueHistoryCache = new Map<string, TonyteActivity[]>();

const VenueProfileScreen: React.FC<Props> = ({ route, navigation }) => {
  const { t } = useI18n();
  const insets = useSafeAreaInsets();
  const [userId, setUserId] = useState<string | null>(auth.currentUser?.uid || null);
  const [resumeCommunityAfterSignIn, setResumeCommunityAfterSignIn] = useState(false);
  const { venueId, venue: passedVenue, embeddedOwnerProfile = false } = route.params;
  const [qrOpen, setQrOpen] = useState(false);
  const { events, venues, venueProfiles, loading } = useVenueVibes();
  // Partner venues can exist with no published events yet (so they're absent
  // from the event-derived `venues`); resolve them from their venues/{id} doc.
  const partnerVenues = useMemo(() => buildPartnerVenueSummaries(events, venueProfiles), [events, venueProfiles]);
  // A partner's profile exists only while its venues/{id} doc does — the
  // server moves it out when the partner is disabled. Never rebuild it from
  // their published events (or a stale nav param) in that case.
  const isPartnerVenueId = useMemo(
    () => Boolean(passedVenue?.ownerUid) || events.some(event => Boolean(event.partnerId) && venueKeyForActivity(event) === venueId),
    [events, passedVenue?.ownerUid, venueId],
  );
  const partnerVenueUnavailable = !loading && isPartnerVenueId && !venueProfiles[venueId];
  const derivedVenue = useMemo(
    () => (partnerVenueUnavailable
      ? null
      : venues.find(item => item.id === venueId) || partnerVenues.find(item => item.id === venueId) || passedVenue || null),
    [partnerVenueUnavailable, partnerVenues, passedVenue, venueId, venues],
  );
  useEffect(() => onAuthStateChanged(auth, user => setUserId(user?.uid ?? null)), []);
  // The shared feed only holds events from today onward; completed events
  // for the Past tab, counts and calendar come from the venue's published
  // history (functions/venueEvents.js — public events only).
  // `settled` = the history for this venue has loaded (or failed), so the
  // counts are final; until then Posts shows a spinner, never the feed-only
  // number (which equals Upcoming and then jumped).
  const [history, setHistory] = useState<{ venueId: string; events: TonyteActivity[]; settled: boolean }>(() => {
    const cached = venueHistoryCache.get(venueId);
    return { venueId, events: cached ?? [], settled: Boolean(cached) };
  });
  const hasVenueDoc = Boolean(venueProfiles[venueId]);
  useEffect(() => {
    if (!hasVenueDoc) return undefined;
    let active = true;
    const cached = venueHistoryCache.get(venueId);
    setHistory(current => (current.venueId === venueId ? current : { venueId, events: cached ?? [], settled: Boolean(cached) }));
    const listEvents = httpsCallable<{ venueId: string }, { events: ({ id: string } & Record<string, unknown>)[] }>(functionsEu, 'listVenueEventsEu');
    listEvents({ venueId })
      .then(({ data }) => {
        const loaded = data.events.map(item => normalizeActivity(item.id, item as never, 'activities'));
        venueHistoryCache.set(venueId, loaded);
        if (active) setHistory({ venueId, events: loaded, settled: true });
      })
      .catch(error => {
        console.warn('[Tonyte][VenueProfile] venue event history failed', error);
        // Fall back to what the feed has rather than spinning forever.
        if (active) setHistory(current => (current.venueId === venueId ? { ...current, settled: true } : { venueId, events: [], settled: true }));
      });
    return () => { active = false; };
  }, [hasVenueDoc, venueId]);
  const historyEvents = useMemo(() => (history.venueId === venueId ? history.events : []), [history, venueId]);
  // A partner venue whose history hasn't arrived yet (including while its
  // venues/{id} doc is still loading). Non-partner venues have no history.
  const historyPending = (hasVenueDoc || (loading && isPartnerVenueId))
    && !(history.venueId === venueId && history.settled);
  // Reviews exist only for partner profiles (a venues/{id} doc).
  const { stats: reviewStats, loaded: reviewStatsLoaded } = usePartnerReviewStats(hasVenueDoc ? venueId : null);
  const listRef = useRef<FlatList<TonyteActivity>>(null);
  const reviewsRef = useRef<ReviewsTabHandle>(null);
  const tabsOffset = useRef({ block: 0, tabs: 0 });

  const venueEvents = useMemo(() => {
    const byId = new Map<string, TonyteActivity>();
    [...historyEvents, ...partnerVenueEvents(events, venueId, derivedVenue?.ownerUid)]
      .forEach(event => byId.set(getResolvedEventId(event), event));
    return [...byId.values()];
  }, [derivedVenue?.ownerUid, events, historyEvents, venueId]);
  const fallbackVenue = useMemo(() => derivedVenue || buildVenueSummaries(venueEvents)[0] || null, [derivedVenue, venueEvents]);
  const { following, followersCount, toggleFollow } = useVenueFollow(fallbackVenue, userId);
  const [tab, setTab] = useState<TabKey>('upcoming');
  const [previewPost, setPreviewPost] = useState<TonyteActivity | null>(null);
  const [followersVisible, setFollowersVisible] = useState(false);
  const [communityBusy, setCommunityBusy] = useState(false);
  const { profile } = useProfile(userId);
  const { appearance, icon, theme } = useAppearance();
  const isAdminViewer = profile?.role === 'admin' || profile?.role === 'co-admin';
  const venueOwnerUid = fallbackVenue?.ownerUid;
  const isOwnerViewer = Boolean(userId) && venueOwnerUid === userId;
  const adminOnlyUnavailable = fallbackVenue?.adminOnly === true && !isAdminViewer && !isOwnerViewer;
  const split = useMemo(() => splitVenueEvents(venueEvents), [venueEvents]);
  // Header stats count the same events as the Upcoming/Past tabs. The venue
  // summary is built from the feed pool, which only holds events from today
  // onward, so on its own it left past events out of "Posts".
  const postsCount = Math.max(fallbackVenue?.postsCount ?? 0, split.upcoming.length + split.past.length);
  const upcomingCount = Math.max(fallbackVenue?.upcomingCount ?? 0, split.upcoming.length);

  useEffect(() => {
    if (!resumeCommunityAfterSignIn || !userId || !fallbackVenue || communityBusy) return;
    setResumeCommunityAfterSignIn(false);
    setCommunityBusy(true);
    joinPartnerCommunity(fallbackVenue.id, false)
      .then(result => navigation.navigate('ChatDetail', { chatId: result.communityId, name: fallbackVenue.name, kind: 'community', photo: fallbackVenue.logoUrl }))
      .catch((error: any) => {
        if (error?.code === 'functions/failed-precondition') {
          Alert.alert(t('partnerCommunity.followToJoinTitle'), t('partnerCommunity.followToJoinBody').replace('{name}', fallbackVenue.name), [
            { text: t('common.cancel'), style: 'cancel' },
            { text: t('partnerCommunity.followAndJoin'), onPress: () => joinPartnerCommunity(fallbackVenue.id, true).then(result => navigation.navigate('ChatDetail', { chatId: result.communityId, name: fallbackVenue.name, kind: 'community', photo: fallbackVenue.logoUrl })).catch(() => Alert.alert(t('common.error'), t('partnerCommunity.joinFailed'))) },
          ]);
        } else Alert.alert(t('common.error'), error?.message || t('partnerCommunity.joinFailed'));
      })
      .finally(() => setCommunityBusy(false));
  }, [communityBusy, fallbackVenue, navigation, resumeCommunityAfterSignIn, t, userId]);

  if (partnerVenueUnavailable || adminOnlyUnavailable) {
    return (
      <TonyteBackground appearanceAware>
        <StatusBar barStyle={theme.statusBarStyle} translucent backgroundColor="transparent" />
        <View style={[styles.unavailable, { paddingTop: insets.top + 12 }]}>
          <TouchableOpacity style={[styles.profileIconButton, appearance.profileIconButton]} onPress={() => navigation.goBack()}>
            <Icon name="chevron-left" size={20} color={icon('#fff')} />
          </TouchableOpacity>
          <View style={styles.unavailableBody}>
            <Icon name="slash" size={28} color={icon('rgba(255,255,255,0.6)')} />
            <Text style={[styles.unavailableText, appearance.emptyText]}>{t('venueProfiles.unavailable')}</Text>
          </View>
        </View>
      </TonyteBackground>
    );
  }
  if (loading && !fallbackVenue) return <LoadingSpinner />;
  if (!fallbackVenue) return <LoadingSpinner />;

  const canManageFollowers = isAdminViewer || isOwnerViewer;
  const posts = tab === 'upcoming' ? split.upcoming : tab === 'past' ? split.past : [];
  const tabs: TabKey[] = hasVenueDoc ? ['upcoming', 'past', 'calendar', 'reviews'] : ['upcoming', 'past', 'calendar'];
  const tabLabel: Record<TabKey, string> = {
    upcoming: t('venueProfiles.upcoming'),
    past: t('venueProfiles.past'),
    calendar: t('venueProfiles.calendar'),
    reviews: t('partnerReviews.tab'),
  };
  // Rating summary → Reviews tab, scrolled so the tab row sits under the
  // status bar.
  const openReviews = () => {
    setTab('reviews');
    const offset = tabsOffset.current.block + tabsOffset.current.tabs - insets.top - 8;
    requestAnimationFrame(() => listRef.current?.scrollToOffset({ offset: Math.max(0, offset), animated: true }));
  };
  const coverSource = fallbackVenue.coverUrl ? { uri: fallbackVenue.coverUrl } : null;
  const logoSource = fallbackVenue.logoUrl ? { uri: fallbackVenue.logoUrl } : null;
  const mapEvent = venueEvents[0];

  const openMap = async () => {
    if (!mapEvent && !fallbackVenue.address) {
      Alert.alert(t('venueProfiles.mapUnavailable'), t('venueProfiles.noAddress'));
      return;
    }
    const url = mapEvent ? buildGoogleMapsUrl(mapEvent) : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${fallbackVenue.name} ${fallbackVenue.address} ${fallbackVenue.city}`)}`;
    await Linking.openURL(url).catch(() => Alert.alert(t('venueProfiles.mapOpenFailed'), t('common.tryAgain')));
  };

  const openCommunity = async (follow = false) => {
    if (!userId) {
      Alert.alert(t('auth.signInRequired'), t('partnerCommunity.signIn'), [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('partnerCommunity.signInAction'), onPress: () => { setResumeCommunityAfterSignIn(true); navigation.navigate('Login'); } },
      ]);
      return;
    }
    if (communityBusy) return;
    setCommunityBusy(true);
    try {
      const result = await joinPartnerCommunity(fallbackVenue.id, follow);
      navigation.navigate('ChatDetail', {
        chatId: result.communityId,
        name: fallbackVenue.name,
        kind: 'community',
        photo: fallbackVenue.logoUrl,
      });
    } catch (error: any) {
      Alert.alert(t('common.error'), error?.message || t('partnerCommunity.joinFailed'));
    } finally {
      setCommunityBusy(false);
    }
  };

  const handleCommunityPress = () => {
    if (!userId || following || isOwnerViewer) {
      void openCommunity(false);
      return;
    }
    Alert.alert(
      t('partnerCommunity.followToJoinTitle'),
      t('partnerCommunity.followToJoinBody').replace('{name}', fallbackVenue.name),
      [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('partnerCommunity.followAndJoin'), onPress: () => void openCommunity(true) },
      ],
    );
  };

  const handleFollowPress = () => {
    if (!following) {
      toggleFollow();
      return;
    }
    Alert.alert(
      t('partnerCommunity.unfollowTitle'),
      t('partnerCommunity.unfollowBody').replace('{name}', fallbackVenue.name),
      [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('partnerCommunity.unfollowAndLeave'), style: 'destructive', onPress: toggleFollow },
      ],
    );
  };

  // Owners edit their own upcoming events from the calendar; admins any. Past
  // events stay read-only (same rule as the Partner Calendar).
  const editEventFor = (event: TonyteActivity) => {
    const ownsEvent = Boolean(userId) && (event.ownerUid === userId || event.createdBy === userId || event.hostId === userId);
    if (!(isAdminViewer || (isOwnerViewer && ownsEvent)) || !isActivityCurrentOrUpcoming(event)) return undefined;
    return () => {
      const edit = (seriesScope?: 'one' | 'future') => navigation.navigate('UploadEvent', { event, mode: 'edit', ...(seriesScope ? { seriesScope } : {}) });
      if (!event.seriesId) {
        edit();
        return;
      }
      Alert.alert(t('partnerCalendar.scopeLegend'), event.title, [
        { text: t('partnerCalendar.scope.one'), onPress: () => edit('one') },
        { text: t('partnerCalendar.scope.future'), onPress: () => edit('future') },
        { text: t('common.cancel'), style: 'cancel' },
      ]);
    };
  };

  const openEvent = (event: TonyteActivity) => {
    setPreviewPost(null);
    trackEventDetailOpen({ eventId: getResolvedEventId(event), organizerId: event.hostId, source: ANALYTICS_SOURCES.PARTNER_PROFILE });
    navigation.navigate('EventDetail', { eventId: getResolvedEventId(event), event });
  };

  return (
    <TonyteBackground appearanceAware>
      <StatusBar barStyle={theme.statusBarStyle} translucent backgroundColor="transparent" />
      {theme.isDark ? <AmbientGlow tone="teal" size={240} intensity={0.18} style={styles.topGlow} /> : null}
      <FlatList
        ref={listRef}
        data={posts}
        keyExtractor={item => getResolvedEventId(item)}
        numColumns={3}
        columnWrapperStyle={styles.postRow}
        ListHeaderComponent={
          <View>
            {/* Full-bleed header: a blurred copy of the cover fills it — the
                status bar area above and a band below — so it reads as one
                image, while the sharp 2.5:1 cover (exactly what the partner
                framed) sits between, clear of the camera cut-out. */}
            <View style={[styles.cover, { height: insets.top + 8 + COVER_HEIGHT + COVER_BOTTOM_BAND }]}>
              {coverSource ? (
                <>
                  <Image source={coverSource} style={StyleSheet.absoluteFill} resizeMode="cover" resizeMethod="resize" blurRadius={24} />
                  <Image source={coverSource} style={[styles.coverImage, { top: insets.top + 8 }]} resizeMode="cover" resizeMethod="resize" />
                </>
              ) : <LinearGradient colors={['#271052', '#090B14']} style={StyleSheet.absoluteFill} />}
              <LinearGradient
                colors={theme.isDark ? ['rgba(3,5,12,0.1)', 'rgba(3,5,12,0.82)'] : ['rgba(245,240,232,0)', 'rgba(245,240,232,0.7)']}
                style={StyleSheet.absoluteFill}
              />
              <View style={[styles.coverNav, { paddingTop: insets.top + 10 }]}>
                {embeddedOwnerProfile ? (
                  <View style={styles.compactThemeSwitch}>
                    <PhysicalLightSwitch scale={0.64} />
                  </View>
                ) : (
                  <TouchableOpacity style={styles.iconButton} onPress={() => navigation.goBack()}>
                    <Icon name="chevron-left" size={22} color="#fff" />
                  </TouchableOpacity>
                )}
                <View style={styles.coverNavActions}>
                  {isOwnerViewer ? (
                    <TouchableOpacity style={styles.iconButton} onPress={() => navigation.navigate('Settings')} accessibilityLabel={t('settings.title')}>
                      <Icon name="settings" size={18} color="#fff" />
                    </TouchableOpacity>
                  ) : null}
                  <TouchableOpacity style={styles.iconButton} onPress={() => setQrOpen(true)}>
                    <Icon name="send" size={18} color="#fff" />
                  </TouchableOpacity>
                </View>
              </View>
            </View>
            <View style={styles.profileBlock} onLayout={event => { tabsOffset.current.block = event.nativeEvent.layout.y; }}>
              <View style={[styles.logoWrap, appearance.logoWrap]}>
                {logoSource ? <Image source={logoSource} style={styles.logoImage} /> : <Text style={[styles.logoLetter, appearance.logoLetter]}>{fallbackVenue.name.slice(0, 1).toUpperCase()}</Text>}
              </View>
              <View style={styles.nameRow}>
                <Text style={[styles.venueName, appearance.venueName]}>{fallbackVenue.name}</Text>
                <Icon name="check-circle" size={18} color={Colors.turquoise} />
              </View>
              <Text style={[styles.username, appearance.username]}>@{fallbackVenue.username}</Text>
              <Text style={[styles.meta, appearance.meta]}>{fallbackVenue.category} · {fallbackVenue.city}</Text>
              <Text style={[styles.address, appearance.address]} numberOfLines={2}>{fallbackVenue.address}</Text>
              {hasVenueDoc ? (
                <RatingSummary stats={reviewStats} loaded={reviewStatsLoaded} canReview={!isOwnerViewer} onPress={openReviews} />
              ) : null}
              <View style={styles.statsRow}>
                <TouchableOpacity style={styles.stat} disabled={!canManageFollowers} onPress={() => setFollowersVisible(true)}>
                  <Text style={[styles.statNumber, appearance.statNumber]}>{followersCount}</Text>
                  <Text style={[styles.statLabel, appearance.statLabel]}>{t('venueProfiles.followers')}</Text>
                </TouchableOpacity>
                <View style={styles.stat}>
                  {historyPending
                    ? <View style={styles.statIcon}><ActivityIndicator size="small" color={icon('#fff')} /></View>
                    : <Text style={[styles.statNumber, appearance.statNumber]}>{postsCount}</Text>}
                  <Text style={[styles.statLabel, appearance.statLabel]}>{t('venueProfiles.posts')}</Text>
                </View>
                <View style={styles.stat}><Text style={[styles.statNumber, appearance.statNumber]}>{upcomingCount}</Text><Text style={[styles.statLabel, appearance.statLabel]}>{t('venueProfiles.upcoming')}</Text></View>
                {canManageFollowers ? (
                  // Owner/admin shortcut to the analytics dashboard, styled as a
                  // stat cell (icon in place of the number) so it adds no height.
                  <TouchableOpacity
                    style={styles.stat}
                    onPress={() => navigation.navigate('VenueAnalytics', { venueId: fallbackVenue.id })}
                    accessibilityRole="button"
                    accessibilityLabel={t('venueProfiles.dashboard')}
                  >
                    <View style={styles.statIcon}>
                      <Icon name="bar-chart-2" size={20} color={theme.isDark ? '#FF9A3C' : theme.colors.accent} />
                    </View>
                    <Text style={[styles.statLabel, appearance.statLabel]}>{t('venueProfiles.analytics')}</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
              <Text style={[styles.bio, appearance.bio]}>{fallbackVenue.bio}</Text>
              <View style={styles.buttonRow}>
                {isOwnerViewer ? (
                  <TouchableOpacity
                    onPress={() => navigation.navigate('EditVenueProfile', { venueId: fallbackVenue.id })}
                    style={[styles.followButton, styles.followingButton, appearance.followingButton]}
                  >
                    <Text style={[styles.followText, appearance.followingText]}>{t('venueProfiles.editProfile')}</Text>
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity onPress={handleFollowPress} style={[styles.followButton, appearance.followButton, following && styles.followingButton, following && appearance.followingButton]}>
                    <Text style={[styles.followText, following && appearance.followingText]}>{following ? t('venueProfiles.following') : t('venueProfiles.follow')}</Text>
                  </TouchableOpacity>
                )}
                {canManageFollowers ? (
                  <TouchableOpacity
                    onPress={() => navigation.navigate('PartnerCalendar', { ownerUid: fallbackVenue.ownerUid, partnerName: fallbackVenue.name })}
                    style={[styles.profileIconButton, appearance.profileIconButton]}
                    accessibilityRole="button"
                    accessibilityLabel={t('partnerCalendar.manage')}
                  >
                    <Icon name="calendar" size={17} color={icon('#fff')} />
                  </TouchableOpacity>
                ) : null}
                <TouchableOpacity onPress={() => setQrOpen(true)} style={[styles.profileIconButton, appearance.profileIconButton]}>
                  <Icon name="send" size={17} color={icon('#fff')} />
                </TouchableOpacity>
                <TouchableOpacity onPress={openMap} style={[styles.profileIconButton, appearance.profileIconButton]}>
                  <Icon name="map-pin" size={17} color={icon('#fff')} />
                </TouchableOpacity>
              </View>
              {hasVenueDoc ? (
                <TouchableOpacity
                  onPress={handleCommunityPress}
                  disabled={communityBusy}
                  style={[styles.communityButton, appearance.followButton, communityBusy && styles.communityButtonDisabled]}
                  accessibilityRole="button"
                >
                  {communityBusy ? <ActivityIndicator size="small" color="#fff" /> : <Icon name="message-circle" size={17} color="#fff" />}
                  <Text style={styles.communityButtonText}>{t('partnerCommunity.button')}</Text>
                </TouchableOpacity>
              ) : null}
              <View style={[styles.tabs, appearance.tabs]} onLayout={event => { tabsOffset.current.tabs = event.nativeEvent.layout.y; }}>
                {tabs.map(item => (
                  <TouchableOpacity
                    key={item}
                    onPress={() => setTab(item)}
                    style={[styles.tabButton, tab === item && styles.tabButtonActive, tab === item && appearance.tabButtonActive]}
                    accessibilityRole="tab"
                    accessibilityState={{ selected: tab === item }}
                    testID={`venue-tab-${item}`}
                  >
                    {/* Four tabs on a narrow phone: shrink long labels (e.g. "Bewertungen") rather than wrap. */}
                    <Text
                      style={[styles.tabText, appearance.tabText, tab === item && styles.tabTextActive, tab === item && appearance.tabTextActive]}
                      numberOfLines={1}
                      adjustsFontSizeToFit
                      minimumFontScale={0.75}
                    >
                      {tabLabel[item]}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
              {tab === 'reviews' ? (
                <ReviewsTab
                  ref={reviewsRef}
                  venueId={venueId}
                  venueName={fallbackVenue.name}
                  userId={userId}
                  isOwner={isOwnerViewer}
                  isAdmin={isAdminViewer}
                  stats={reviewStats}
                  statsLoaded={reviewStatsLoaded}
                />
              ) : null}
              {tab === 'calendar' ? (
                <VenueCalendar
                  // Published events only — drafts are never marked or listed.
                  events={venueEvents}
                  onOpenEvent={openEvent}
                  onEditEvent={editEventFor}
                  footer={canManageFollowers ? (
                    <>
                    <TouchableOpacity
                      style={[styles.importsButton, appearance.importsButton]}
                      onPress={() => navigation.navigate('PartnerCalendar', { ownerUid: fallbackVenue.ownerUid, partnerName: fallbackVenue.name })}
                      activeOpacity={0.85}
                      accessibilityRole="button"
                    >
                      <View style={styles.importsIcon}>
                        <Icon name="grid" size={16} color="#FF9A3C" />
                      </View>
                      <View style={styles.importsCopy}>
                        <Text style={[styles.importsTitle, appearance.calendarEventTitle]}>{t('partnerCalendar.manage')}</Text>
                        <Text style={[styles.importsSubtitle, appearance.calendarEmpty]} numberOfLines={1}>{t('partnerCalendar.entrySub')}</Text>
                      </View>
                      <Icon name="chevron-right" size={16} color={icon('rgba(255,255,255,0.5)')} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.importsButton, appearance.importsButton]}
                      onPress={() => navigation.navigate('CalendarImports', { ownerUid: fallbackVenue.ownerUid, venueName: fallbackVenue.name })}
                      activeOpacity={0.85}
                    >
                      <View style={styles.importsIcon}>
                        <Icon name="download" size={16} color="#FF9A3C" />
                      </View>
                      <View style={styles.importsCopy}>
                        <Text style={[styles.importsTitle, appearance.calendarEventTitle]}>{t('venueProfiles.calendarImports')}</Text>
                        <Text style={[styles.importsSubtitle, appearance.calendarEmpty]} numberOfLines={1}>{t('venueProfiles.calendarImportsSub')}</Text>
                      </View>
                      <Icon name="chevron-right" size={16} color={icon('rgba(255,255,255,0.5)')} />
                    </TouchableOpacity>
                    </>
                  ) : null}
                />
              ) : null}
            </View>
          </View>
        }
        renderItem={({ item }) => (
          <EventPostTile
            event={item}
            onPress={() => openEvent(item)}
            onLongPress={() => setPreviewPost(item)}
          />
        )}
        ListEmptyComponent={tab === 'calendar' || tab === 'reviews' ? null : tab === 'past' && historyPending ? (
          <View style={styles.empty}><ActivityIndicator color={Colors.purple} /></View>
        ) : (
          <View style={styles.empty}>
            <Icon name="calendar" size={24} color={Colors.purple} />
            <Text style={[styles.emptyText, appearance.emptyText]}>{tab === 'upcoming' ? 'No upcoming venue posts yet.' : 'No past venue posts yet.'}</Text>
          </View>
        )}
        ListFooterComponent={<View style={{ height: insets.bottom + 30 }} />}
        onEndReached={tab === 'reviews' ? () => reviewsRef.current?.loadMore() : undefined}
        onEndReachedThreshold={0.4}
        showsVerticalScrollIndicator={false}
      />
      <PostPreviewModal
        event={previewPost}
        visible={Boolean(previewPost)}
        onClose={() => setPreviewPost(null)}
        onOpenEvent={openEvent}
      />
      {canManageFollowers ? (
        <FollowersModal venue={fallbackVenue} visible={followersVisible} onClose={() => setFollowersVisible(false)} />
      ) : null}
      <PartnerQrSheet venue={fallbackVenue} visible={qrOpen} onClose={() => setQrOpen(false)} />
    </TonyteBackground>
  );
};

const styles = StyleSheet.create({
  topGlow: { top: -130, right: -100 },
  cover: {
    overflow: 'hidden',
    backgroundColor: '#080A13',
  },
  coverImage: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: COVER_HEIGHT,
  },
  coverNav: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  coverNavActions: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  iconButton: {
    width: 39,
    height: 39,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.42)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
  },
  compactThemeSwitch: {
    width: 68,
    height: 54,
    alignItems: 'center',
    justifyContent: 'center',
  },
  profileBlock: {
    paddingHorizontal: 18,
    paddingBottom: 16,
    marginTop: -42,
  },
  communityButton: { marginTop: 12, minHeight: 50, borderRadius: 16, flexDirection: 'row', gap: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: Colors.purple, borderWidth: 1, borderColor: 'rgba(255,255,255,0.16)' },
  communityButtonDisabled: { opacity: 0.6 },
  communityButtonText: { color: '#fff', fontSize: 15, fontWeight: Typography.extrabold },
  logoWrap: {
    width: 88,
    height: 88,
    borderRadius: 44,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#111827',
    borderWidth: 3,
    borderColor: '#080A13',
    overflow: 'hidden',
  },
  logoImage: { width: '100%', height: '100%' },
  logoLetter: { color: '#fff', fontSize: 34, fontWeight: Typography.extrabold },
  nameRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 12 },
  venueName: { color: '#fff', fontSize: 25, lineHeight: 30, fontWeight: Typography.extrabold, flexShrink: 1 },
  username: { color: 'rgba(255,255,255,0.62)', fontSize: 14, marginTop: 2 },
  meta: { color: '#D8B4FE', fontSize: 13, fontWeight: Typography.bold, marginTop: 10 },
  address: { color: 'rgba(255,255,255,0.62)', fontSize: 13, lineHeight: 18, marginTop: 3 },
  statsRow: {
    marginTop: 17,
    flexDirection: 'row',
  },
  stat: { flex: 1, alignItems: 'center', paddingVertical: 12 },
  statNumber: { color: '#fff', fontSize: 18, fontWeight: Typography.extrabold },
  statLabel: { color: 'rgba(255,255,255,0.54)', fontSize: 11, marginTop: 2 },
  bio: { color: 'rgba(255,255,255,0.78)', fontSize: 14, lineHeight: 20, marginTop: 15 },
  buttonRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 16 },
  followButton: {
    flex: 1,
    height: 44,
    borderRadius: Radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#8B5CF6',
  },
  followingButton: {
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
  },
  followText: { color: '#fff', fontSize: 14, fontWeight: Typography.extrabold },
  profileIconButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
  },
  tabs: {
    flexDirection: 'row',
    marginTop: 18,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.10)',
  },
  tabButton: { flex: 1, alignItems: 'center', paddingVertical: 13, paddingHorizontal: 3, borderBottomWidth: 2, borderBottomColor: 'transparent' },
  tabButtonActive: { borderBottomColor: '#D8B4FE' },
  tabText: { color: 'rgba(255,255,255,0.52)', fontSize: 13, fontWeight: Typography.bold },
  tabTextActive: { color: '#fff' },
  postRow: { gap: GRID_GAP },
  postTile: {
    width: POST_SIZE,
    height: POST_SIZE,
    marginBottom: GRID_GAP,
    overflow: 'hidden',
    backgroundColor: '#111827',
  },
  postTitle: {
    position: 'absolute',
    left: 7,
    right: 7,
    bottom: 25,
    color: '#fff',
    fontSize: 11,
    lineHeight: 14,
    fontWeight: Typography.extrabold,
  },
  postStats: {
    position: 'absolute',
    left: 7,
    bottom: 7,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  postStatsText: { color: '#fff', fontSize: 10, fontWeight: Typography.bold, marginRight: 4 },
  collabPill: { position: 'absolute', left: 7, right: 7, top: 7, minHeight: 20, borderRadius: 10, paddingHorizontal: 6, flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: 'rgba(0,0,0,0.42)' },
  collabText: { flex: 1, color: '#fff', fontSize: 9, fontWeight: Typography.extrabold },
  empty: { paddingVertical: 60, alignItems: 'center' },
  emptyText: { color: 'rgba(255,255,255,0.62)', marginTop: 10 },
  followersSheet: {
    maxHeight: '75%',
    borderRadius: 22,
    padding: 16,
    backgroundColor: '#101722',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  followersTitle: { flex: 1, color: '#fff', fontSize: 18, fontWeight: Typography.extrabold },
  followersStatus: { paddingVertical: 24 },
  followersEmpty: { color: 'rgba(255,255,255,0.6)', fontSize: 13, textAlign: 'center' },
  followerRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 10 },
  followerAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1b2331',
  },
  followerAvatarImage: { width: '100%', height: '100%' },
  followerAvatarText: { color: '#fff', fontSize: 16, fontWeight: Typography.extrabold },
  followerText: { flex: 1, minWidth: 0 },
  followerName: { color: '#fff', fontSize: 14, fontWeight: Typography.bold },
  followerUsername: { color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 1 },
  followerRemove: {
    minWidth: 78,
    height: 32,
    borderRadius: Radius.pill,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
  },
  followerRemoveText: { color: '#fff', fontSize: 12, fontWeight: Typography.bold },
  // Same height as statNumber's line so the Analytics cell lines up.
  statIcon: { height: 22, justifyContent: 'center' },
  calendar: { paddingVertical: 16 },
  calendarToggleRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 },
  calendarToggle: { flexDirection: 'row', alignItems: 'center', gap: 6, borderRadius: 999, paddingHorizontal: 14, paddingVertical: 8, backgroundColor: 'rgba(255,255,255,0.08)' },
  calendarToggleActive: { backgroundColor: '#A855F7' },
  calendarToggleText: { color: '#fff', fontSize: 13, fontWeight: Typography.bold },
  calendarScope: { color: 'rgba(255,255,255,0.55)', fontSize: 13, fontWeight: Typography.bold, paddingVertical: 4, paddingRight: 10 },
  calendarScopeActive: { color: '#fff', textDecorationLine: 'underline' },
  calendarListEvents: { gap: 12 },
  agendaDay: { flexDirection: 'row' },
  agendaRail: { width: 18, alignItems: 'center', paddingTop: 6 },
  agendaDot: { width: 9, height: 9, borderRadius: 5, backgroundColor: 'rgba(255,255,255,0.45)' },
  agendaDotToday: { backgroundColor: '#F27D26' },
  agendaLine: { flex: 1, width: 1, marginTop: 6, backgroundColor: 'rgba(255,255,255,0.14)' },
  agendaBody: { flex: 1, minWidth: 0, paddingLeft: 10, paddingBottom: 22 },
  agendaHeading: { marginBottom: 10 },
  agendaDate: { color: '#fff', fontSize: 16, fontWeight: Typography.extrabold },
  agendaWeekday: { color: 'rgba(255,255,255,0.55)', fontSize: 15, fontWeight: Typography.semibold, textTransform: 'capitalize' },
  agendaWeekdayToday: { color: '#F27D26' },
  agendaCard: {
    flexDirection: 'row',
    gap: 12,
    padding: 14,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  agendaCardCopy: { flex: 1, minWidth: 0, gap: 5 },
  agendaTime: { color: 'rgba(255,255,255,0.55)', fontSize: 13, fontWeight: Typography.semibold },
  agendaTitle: { color: '#fff', fontSize: 16.5, lineHeight: 21, fontWeight: Typography.extrabold },
  agendaMetaRow: { flexDirection: 'row', alignItems: 'center', gap: 6, minWidth: 0 },
  agendaMeta: { flexShrink: 1, color: 'rgba(255,255,255,0.6)', fontSize: 13 },
  agendaHostLogo: { width: 16, height: 16, borderRadius: 8 },
  agendaHostFallback: { backgroundColor: '#A855F7', alignItems: 'center', justifyContent: 'center' },
  agendaHostLetter: { color: '#fff', fontSize: 9, fontWeight: Typography.extrabold },
  agendaFooter: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 4 },
  agendaEdit: { flexDirection: 'row', alignItems: 'center', gap: 5, marginLeft: 'auto', borderRadius: 999, borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)', paddingHorizontal: 10, paddingVertical: 5 },
  agendaEditText: { fontSize: 12, fontWeight: Typography.bold },
  agendaPrice: { borderRadius: 6, paddingHorizontal: 7, paddingVertical: 2, backgroundColor: 'rgba(242,125,38,0.16)' },
  agendaPriceText: { color: '#FF9A3C', fontSize: 12, fontWeight: Typography.extrabold },
  agendaPriceFree: { backgroundColor: 'rgba(34,197,94,0.16)' },
  agendaPriceFreeText: { color: '#22C55E' },
  agendaCover: { width: 88, height: 88, borderRadius: 14, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  calendarHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  calendarNav: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  calendarMonth: { color: '#fff', fontSize: 16, fontWeight: Typography.extrabold, textTransform: 'capitalize' },
  calendarGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  calendarWeekday: { width: CALENDAR_CELL, textAlign: 'center', color: 'rgba(255,255,255,0.45)', fontSize: 11, fontWeight: Typography.bold, marginBottom: 6 },
  calendarCell: { width: CALENDAR_CELL, height: 44, alignItems: 'center', justifyContent: 'center' },
  calendarDay: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  calendarToday: { borderWidth: 1, borderColor: 'rgba(216,180,254,0.6)' },
  calendarSelected: { backgroundColor: '#A855F7', borderWidth: 0 },
  calendarDayText: { color: '#fff', fontSize: 13, fontWeight: Typography.bold },
  calendarDayMuted: { color: 'rgba(255,255,255,0.45)', fontWeight: Typography.medium },
  calendarDayTextSelected: { color: '#fff' },
  calendarDot: { width: 5, height: 5, borderRadius: 3, marginTop: 2, backgroundColor: Colors.turquoise },
  calendarDotHidden: { opacity: 0 },
  calendarEvents: { marginTop: 12, gap: 8 },
  calendarEvent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  calendarEventTime: { color: '#D8B4FE', fontSize: 13, fontWeight: Typography.extrabold, minWidth: 44 },
  calendarEventTitle: { flex: 1, color: '#fff', fontSize: 14, fontWeight: Typography.bold },
  unavailable: { flex: 1, paddingHorizontal: 18 },
  unavailableBody: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12, paddingBottom: 80 },
  unavailableText: { color: 'rgba(255,255,255,0.62)', fontSize: 15, textAlign: 'center' },
  importsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 16,
    padding: 12,
    borderRadius: 16,
    backgroundColor: 'rgba(255,122,0,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,154,60,0.3)',
  },
  importsIcon: { width: 34, height: 34, borderRadius: 11, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,122,0,0.15)' },
  importsCopy: { flex: 1, minWidth: 0 },
  importsTitle: { color: '#fff', fontSize: 14, fontWeight: Typography.extrabold },
  importsSubtitle: { color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 1, textAlign: 'left', paddingVertical: 0 },
  calendarEmpty: { color: 'rgba(255,255,255,0.55)', fontSize: 13, textAlign: 'center', paddingVertical: 12 },
  previewBackdrop: { flex: 1, justifyContent: 'center', paddingHorizontal: 18, backgroundColor: 'rgba(0,0,0,0.66)' },
  previewCard: {
    borderRadius: 26,
    backgroundColor: '#080A13',
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(216,180,254,0.24)',
  },
  modalBackdrop: { flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.62)' },
  modalCard: {
    maxHeight: '92%',
    borderTopLeftRadius: 26,
    borderTopRightRadius: 26,
    backgroundColor: '#080A13',
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  modalHandle: { alignSelf: 'center', width: 42, height: 4, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.28)', marginTop: 10, marginBottom: 8 },
  modalImage: { width: '100%', height: Math.min(300, width * 0.72), backgroundColor: '#111827' },
  modalBody: { padding: 16, paddingBottom: 28 },
  modalHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
  modalTitle: { color: '#fff', fontSize: 20, fontWeight: Typography.extrabold, lineHeight: 25 },
  modalMeta: { color: 'rgba(255,255,255,0.58)', fontSize: 13, marginTop: 3 },
  closeButton: { width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.08)' },
  actionRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 16 },
  actionButton: { height: 38, minWidth: 58, borderRadius: 19, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: 'rgba(255,255,255,0.09)' },
  actionText: { color: '#fff', fontSize: 12, fontWeight: Typography.bold },
  eventButton: { marginLeft: 'auto', height: 38, borderRadius: 19, paddingHorizontal: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: '#8B5CF6' },
  eventButtonText: { color: '#fff', fontSize: 12, fontWeight: Typography.extrabold },
  commentInputWrap: { flexDirection: 'row', gap: 10, marginTop: 18, alignItems: 'flex-end' },
  commentInput: { flex: 1, minHeight: 42, maxHeight: 92, color: '#fff', borderRadius: 18, paddingHorizontal: 14, paddingVertical: 10, backgroundColor: 'rgba(255,255,255,0.08)' },
  commentSend: { width: 42, height: 42, borderRadius: 21, alignItems: 'center', justifyContent: 'center', backgroundColor: '#8B5CF6' },
  commentsTitle: { color: '#fff', fontSize: 15, fontWeight: Typography.extrabold, marginTop: 18 },
  commentRow: { flexDirection: 'row', gap: 10, marginTop: 13 },
  commentAvatar: { width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.1)', overflow: 'hidden' },
  commentAvatarImage: { width: '100%', height: '100%' },
  commentName: { color: '#fff', fontSize: 12, fontWeight: Typography.bold },
  commentText: { color: 'rgba(255,255,255,0.72)', fontSize: 13, lineHeight: 18, marginTop: 2 },
  commentDelete: { width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.08)' },
});

const createThemeStyles = (theme: TonyteTheme) => {
  const c = theme.colors;
  const accentText = '#C95812';
  return StyleSheet.create({
    logoWrap: daylightStyle(theme, { backgroundColor: c.surfaceElevated, borderColor: c.background }),
    logoLetter: daylightStyle(theme, { color: c.textPrimary }),
    venueName: daylightStyle(theme, { color: c.textPrimary }),
    username: daylightStyle(theme, { color: c.textSecondary }),
    meta: daylightStyle(theme, { color: accentText }),
    address: daylightStyle(theme, { color: c.textSecondary }),
    statNumber: daylightStyle(theme, { color: c.textPrimary }),
    statLabel: daylightStyle(theme, { color: c.textSecondary }),
    bio: daylightStyle(theme, { color: c.bioText }),
    followButton: daylightStyle(theme, { backgroundColor: c.accent }),
    followingButton: daylightStyle(theme, { backgroundColor: c.controlBackground, borderColor: c.borderStrong }),
    followingText: daylightStyle(theme, { color: c.textPrimary }),
    profileIconButton: daylightStyle(theme, { backgroundColor: c.controlBackground, borderColor: c.border }),
    tabs: daylightStyle(theme, { borderBottomColor: c.divider }),
    tabButtonActive: daylightStyle(theme, { borderBottomColor: c.accent }),
    tabText: daylightStyle(theme, { color: c.textMuted }),
    tabTextActive: daylightStyle(theme, { color: c.textPrimary }),
    emptyText: daylightStyle(theme, { color: c.textSecondary }),
    calendarNav: daylightStyle(theme, { backgroundColor: c.controlBackground }),
    calendarMonth: daylightStyle(theme, { color: c.textPrimary }),
    calendarWeekday: daylightStyle(theme, { color: c.textMuted }),
    calendarToday: daylightStyle(theme, { borderColor: 'rgba(242,125,38,0.55)' }),
    calendarSelected: daylightStyle(theme, { backgroundColor: c.accent }),
    calendarDayText: daylightStyle(theme, { color: c.textPrimary }),
    calendarDayMuted: daylightStyle(theme, { color: c.textMuted }),
    calendarDot: daylightStyle(theme, { backgroundColor: c.accentSecondary }),
    calendarEvent: daylightStyle(theme, { backgroundColor: c.cardBackground, borderColor: c.cardBorder }),
    calendarEventTime: daylightStyle(theme, { color: accentText }),
    calendarEventTitle: daylightStyle(theme, { color: c.textPrimary }),
    importsButton: daylightStyle(theme, { backgroundColor: c.cardBackground, borderColor: 'rgba(242,125,38,0.3)', ...theme.shadows.card }),
    calendarEmpty: daylightStyle(theme, { color: c.textSecondary }),
    agendaCard: daylightStyle(theme, { backgroundColor: c.cardBackground, borderColor: c.cardBorder, ...theme.shadows.card }),
    agendaEdit: daylightStyle(theme, { borderColor: c.cardBorder }),
    agendaDot: daylightStyle(theme, { backgroundColor: c.textMuted }),
    agendaLine: daylightStyle(theme, { backgroundColor: c.divider }),
    previewBackdrop: daylightStyle(theme, { backgroundColor: c.overlay }),
    previewCard: daylightStyle(theme, { backgroundColor: c.surfaceElevated, borderColor: c.cardBorder }),
    modalTitle: daylightStyle(theme, { color: c.textPrimary }),
    modalMeta: daylightStyle(theme, { color: c.textSecondary }),
    closeButton: daylightStyle(theme, { backgroundColor: c.controlBackground }),
    actionButton: daylightStyle(theme, { backgroundColor: c.controlBackground }),
    actionText: daylightStyle(theme, { color: c.actionLabel }),
    followersSheet: daylightStyle(theme, { backgroundColor: c.surfaceElevated, borderColor: c.cardBorder }),
    followersTitle: daylightStyle(theme, { color: c.textPrimary }),
    followersEmpty: daylightStyle(theme, { color: c.textSecondary }),
    followerAvatar: daylightStyle(theme, { backgroundColor: c.backgroundSecondary }),
    followerAvatarText: daylightStyle(theme, { color: c.textPrimary }),
    followerName: daylightStyle(theme, { color: c.textPrimary }),
    followerUsername: daylightStyle(theme, { color: c.textSecondary }),
    followerRemove: daylightStyle(theme, { backgroundColor: c.controlBackground, borderColor: c.borderStrong }),
    followerRemoveText: daylightStyle(theme, { color: c.error }),
  });
};

export default VenueProfileScreen;
